/**
 * Lets a page that is about to route on its own wait for the native deep-link
 * decision first.
 *
 * On a cold start from a Universal Link the app loads `/` -> `/resume`, and two
 * things race: `DeepLinkListener` reads the launch URL and assigns
 * `window.location.href`, while `/resume` fetches the last route and calls
 * `router.replace`. In WebKit — i.e. in the iOS app — a client-side route change
 * CANCELS a document navigation that is still pending, so the tapped link's
 * request went out, its response was thrown away, and the user landed on their
 * last route every time. (Chromium lets the document navigation win, which is
 * why this never reproduced on a desktop.)
 *
 * `DeepLinkListener` settles this once per page load: `true` when it has started
 * navigating to a deep link, `false` when there is nothing to act on — on the
 * web that is a few milliseconds after mount.
 */
let settled = false
let settle: (navigating: boolean) => void = () => {}
const decision = new Promise<boolean>((resolve) => {
  settle = resolve
})

export function settleDeepLinkDecision(navigating: boolean): void {
  if (settled) return
  settled = true
  settle(navigating)
}

/**
 * Resolves `true` when a deep-link navigation is under way and the caller must
 * not route. Never hangs: with no verdict inside `timeoutMs` (listener not
 * mounted, native bridge wedged) it resolves `false` and the caller carries on.
 */
export function deepLinkTookOver(timeoutMs = 3000): Promise<boolean> {
  return Promise.race([
    decision,
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ])
}
